import { useEffect, useRef, useState } from 'react';
import { debounce, isObject } from 'underscore';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import Switch from '@ui/switch/switch';
import TextArea from '@ui/text-area/text-area';
import UploadLarge from '@ui/uploads/upload-large';
import handleImageUploadErrors from '@ui/image-upload/handle-image-upload-errors';
import {
  GetUploadEndpoint,
  getUploadFilename,
  UploadFileToProvider,
  SetItemContentType,
} from '@ui/uploads/validations';
import { ICONS } from '@ui/icons/types';
import { validateUrl } from '@/utils/validate-url';
import type { ResolvedEmailBlock } from '../../../templates/widgets/block-types';
import { TYPING_DEBOUNCE_MS } from '../../constants';
import ColorPill from '../controls/color-pill';
import NumberField from '../controls/number-field';
import TextField from '../controls/text-field';
import AlignmentControl from '../controls/alignment-control';
import BoxModelControl from '../controls/box-model-control';
import { useBoxModelStyle } from '../controls/use-box-model-style';
import { useResolvedBlockStyle } from '../controls/use-resolved-block-style';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setCampaignEditorField } from '@/store/slices/campaign-editor';
import { selectHideBrandliveLogo } from '@/store/slices/campaign-editor/selectors';

interface FooterStylePanelProps {
  block: ResolvedEmailBlock;
  onUpdateStyle: (key: string, value: unknown) => void;
  onUpdateSrc: (src: string) => void;
  onUpdateProp: (key: string, value: unknown) => void;
}

function ToggleRow({ label, on, onChange }: { label: string; on: boolean; onChange: (on: boolean) => void }) {
  return (
    <Container
      className="flex ai-center jc-space-between"
      padding="12px 16px"
      background="var(--gray-900)"
      borderRadius="12px"
      design="GlobalCont2"
    >
      <Text className="P2" color="var(--global-ico-text-2)">
        {label}
      </Text>
      <Switch value={label} on={on} onClick={(_, next) => onChange(next)} />
    </Container>
  );
}

// Implementation of the Figma "Footer" sidepane. Mirrors ImageStylePanel: the card's visual overrides
// live on block.style; its content (logo src, text, legal link) lives on block.props.
export default function FooterStylePanel({ block, onUpdateStyle, onUpdateSrc, onUpdateProp }: FooterStylePanelProps) {
  const dispatch = useAppDispatch();
  // the campaign-level setting - the same one the top-level "Hide Brandlive Logo" switch drives
  const hideBrandliveLogo = useAppSelector(selectHideBrandliveLogo);
  const style = block.style ?? {};
  const resolved = useResolvedBlockStyle(block.id, block.style);
  const [uploading, setUploading] = useState(false);

  const src = block.props.src as string | undefined;
  const value = (block.props.value as string | undefined) ?? '';
  const showLegalLink = !!block.props.showLegalLink;
  const privacyPolicyUrl = (block.props.privacyPolicyUrl as string | undefined) ?? '';
  const align = (style.align as string | undefined) ?? 'left';

  const [text, setText] = useState(value);
  const [textFocused, setTextFocused] = useState(false);
  const [url, setUrl] = useState(privacyPolicyUrl);

  // keeps up with edits made on the canvas itself, but never while this field is being typed in
  useEffect(() => {
    if (!textFocused) setText(value);
  }, [value, textFocused]);

  const onUpdatePropRef = useRef(onUpdateProp);
  useEffect(() => {
    onUpdatePropRef.current = onUpdateProp;
  }, [onUpdateProp]);
  const commitText = useRef(
    debounce((next: string) => onUpdatePropRef.current('value', next), TYPING_DEBOUNCE_MS)
  ).current;

  const padding = useBoxModelStyle({
    blockId: block.id,
    styleValue: style.padding,
    resolvedValues: [resolved.paddingTop, resolved.paddingRight, resolved.paddingBottom, resolved.paddingLeft],
    styleKey: 'padding',
    uniformity: 'paired',
    onUpdateStyle,
  });

  const handleFile = async (file: File | FileList) => {
    if (!(file instanceof File)) return;
    setUploading(true);
    try {
      const endpoint = await GetUploadEndpoint({ filename: getUploadFilename(file.name) });
      if (isObject(endpoint) && endpoint.url) {
        const finalUrl = await UploadFileToProvider(
          { url: endpoint.url, fields: endpoint.fields, cdnHost: endpoint?.cdnHost },
          file
        );
        SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);
        if (finalUrl) onUpdateSrc(finalUrl);
      }
    } catch (error) {
      console.error(error);
      handleImageUploadErrors(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container className="flex-column gap-16" padding="0 16px">
      <Container width="308px" height="172px">
        <UploadLarge
          displayImageUrl={src || undefined}
          uploading={uploading}
          onFile={handleFile}
          hidePlaceholderImage
          deletable
          actionButtonsType="cover"
          onDelete={() => onUpdateSrc('')}
          style={{
            width: '308px',
            height: '172px',
            padding: '0 24px',
            borderRadius: '16px',
            border: '1px dashed var(--global-ico-text-3)',
            backgroundColor: 'transparent',
            backgroundOrigin: 'content-box',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
          allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png']}
          maxSizeInBytes={1024 * 1024 * 5}
          CustomTitle={
            <Container className="flex-column ai-center gap-4">
              <Container className="flex ai-center gap-6">
                <Icon name={ICONS.UPLOAD} size={12} color="var(--global-ico-text-1)" />
                <Text className="S2" color="var(--global-ico-text-1)">
                  Upload Logo
                </Text>
              </Container>
              <Text className="C2" color="var(--global-ico-text-3)">
                or drag and drop them here
              </Text>
              <Text className="C2" color="var(--global-ico-text-3)">
                5mb | png, jpg, jpeg
              </Text>
            </Container>
          }
        />
      </Container>

      <Container className="flex gap-12">
        <ColorPill
          label="Background color"
          value={(style.background as string | undefined) ?? resolved.background}
          isOverridden={style.background !== undefined}
          onChange={(v) => onUpdateStyle('background', v)}
          onReset={() => onUpdateStyle('background', undefined)}
        />
        <AlignmentControl value={align} onChange={(v) => onUpdateStyle('align', v)} />
      </Container>

      <BoxModelControl
        sectionLabel="Padding"
        expanded={padding.expanded}
        onToggleExpand={() => padding.setExpanded((prev) => !prev)}
        toggleIcon={ICONS.PADDING_SIDES}
        toggleLabel="padding controls"
        values={padding.values}
        onChangeSlot={padding.setSlot}
        fields={[
          { slot: 0, icon: ICONS.PADDING_TOP, ariaLabel: 'Padding top' },
          { slot: 1, icon: ICONS.PADDING_RIGHT, ariaLabel: 'Padding right' },
          { slot: 2, icon: ICONS.PADDING_BOTTOM, ariaLabel: 'Padding bottom' },
          { slot: 3, icon: ICONS.PADDING_LEFT, ariaLabel: 'Padding left' },
        ]}
        renderCollapsed={() => (
          <Container className="flex gap-12 ai-flex-end">
            <NumberField
              label="Padding"
              icon={ICONS.PADDING_VERTICAL}
              allowNegative={false}
              min={0}
              max={100}
              value={padding.values[0]}
              onChange={padding.setCollapsedFirst}
              debounceTime={TYPING_DEBOUNCE_MS}
            />
            <NumberField
              icon={ICONS.PADDING_HORIZONTAL}
              allowNegative={false}
              min={0}
              max={100}
              value={padding.values[1]}
              onChange={padding.setCollapsedSecond}
              debounceTime={TYPING_DEBOUNCE_MS}
            />
          </Container>
        )}
      />

      <TextArea
        label="Text"
        placeholder="COMPANY NAME ..."
        minRows={1}
        value={text}
        onFocus={() => setTextFocused(true)}
        onChange={(e) => {
          setText(e.target.value);
          commitText(e.target.value);
        }}
        onBlur={() => {
          // lands whatever is still waiting, so the field can't snap back to the older value on blur
          commitText.cancel();
          if (text !== value) onUpdateProp('value', text);
          setTextFocused(false);
        }}
      />

      <ToggleRow
        label="Brandlive Logo"
        on={!hideBrandliveLogo}
        onChange={(on) => dispatch(setCampaignEditorField({ key: 'hideBrandliveLogo', value: !on }))}
      />

      <ToggleRow label="Legal Link" on={showLegalLink} onChange={(on) => onUpdateProp('showLegalLink', on)} />

      {showLegalLink && (
        <TextField
          label="Privacy Policy Link URL"
          placeholder="https://www.company.com/privacy"
          value={url}
          onChange={setUrl}
          onDebounce={(v) => onUpdateProp('privacyPolicyUrl', v === '' ? undefined : v)}
          debounceTime={TYPING_DEBOUNCE_MS}
          helperText={url && !validateUrl(url) ? 'Enter a full URL, starting with https://' : undefined}
        />
      )}
    </Container>
  );
}
